import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../store";
import { addCargoSystem } from "../../../../store/features/optionalSystemsSlice";
import { CargoSystem } from "../../../../types/optionalSystems";
import { validateCargoSystem } from "../../../../utils/calculations/optionalSystems";
import { formatCurrency } from "../../../../utils/formatting";

import {
  selectAvailableCargoSpace,
  selectCargoCraneStats,
  selectExternalCargoMountStats,
  selectJumpNetStats,
} from '../../../../store/selectors/derivedSelectors';

interface CargoSystemsProps {
  availableSystems: CargoSystem[];
  shipTonnage: number;
  techLevel: number;
  isStreamlined: boolean;
  isDispersedStructure: boolean;
  tlOverrideActive?: boolean;
}

// Mapping from subtype to display name
const CARGO_SYSTEM_NAMES: Record<string, string> = {
  cargoCrane: "Cargo Crane",
  cargoNet: "Cargo Net",
  cargoScoop: "Cargo Scoop",
  externalCargoMount: "External Cargo Mount",
  jumpNetInterplanetary: "Jump Net",
  jumpNetInterstellar: "Jump Net",
  loadingBelt7: "Loading Belt",
  loadingBelt12: "Loading Belt",
};

const CargoSystems: React.FC<CargoSystemsProps> = ({
  availableSystems,
  shipTonnage,
  techLevel,
  isStreamlined,
  isDispersedStructure,
  tlOverrideActive = false,
}) => {
  const dispatch = useAppDispatch();
  const systems = useAppSelector((state) => state.optionalSystems.cargo);

  // Use selectors for cargo calculations
  const availableCargoSpace = useAppSelector(selectAvailableCargoSpace);
  const cargoCraneStats = (useAppSelector(selectCargoCraneStats) || {}) as any;
  const externalCargoMountStats = (useAppSelector(selectExternalCargoMountStats) || {}) as any;
  const jumpNetStats = (useAppSelector(selectJumpNetStats) || {}) as any;

  // Calculate drive values for effective tonnage

  const [selectedSystem, setSelectedSystem] = useState<Partial<CargoSystem>>({
    type: "cargo",
    subtype: "cargoCrane",
    name: CARGO_SYSTEM_NAMES["cargoCrane"],
    cost: 0,
    tonnage: 0,
    power: 0,
    properties: {},
  });

  // User input values (only for systems that need it)
  const [externalCargoCapacity, setExternalCargoCapacity] = useState<number>(0);
  const [jumpNetCargoCapacity, setJumpNetCargoCapacity] = useState<number>(0);

  // Add this at the top level of the CargoSystems component, after other useState hooks
  const [jumpNetType, setJumpNetType] = useState<
    "jumpNetInterplanetary" | "jumpNetInterstellar"
  >("jumpNetInterplanetary");
  const [loadingBeltType, setLoadingBeltType] = useState<
    "loadingBelt7" | "loadingBelt12"
  >("loadingBelt7");

  // Validation logic
  const validationErrors: string[] = [];

  if (!selectedSystem.subtype) {
    validationErrors.push("Please select a cargo system type.");
  }

  // System-specific validation
  if (selectedSystem.subtype) {
    const systemValidation = validateCargoSystem(
      selectedSystem as CargoSystem,
      shipTonnage,
      techLevel,
      isStreamlined,
      isDispersedStructure,
      tlOverrideActive
    );
    validationErrors.push(...systemValidation);
  }

  // Duplicate check
  if (
    selectedSystem.subtype &&
    systems.some((s) => s.subtype === selectedSystem.subtype)
  ) {
    validationErrors.push("This cargo system is already installed.");
  }

  const handleSubtypeChange = (subtype: string) => {
    setSelectedSystem({
      type: "cargo",
      subtype: subtype as any,
      name: CARGO_SYSTEM_NAMES[subtype] || subtype,
      cost: 0,
      tonnage: 0,
      power: 0,
      properties: {},
    });
    // Reset user inputs
    setExternalCargoCapacity(0);
    setJumpNetCargoCapacity(0);
  };

  // Remove all calculation logic for cargo system stats and drive preview from the component.
  // Use the above selectors for all calculated values in the UI.

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Cargo Systems</h2>
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 bg-white dark:bg-gray-800">
        <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Available Cargo Systems</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {availableSystems
            .filter((system) => techLevel >= system.techLevel)
            .filter(
              (system) =>
                ![
                  "jumpNetInterplanetary",
                  "jumpNetInterstellar",
                  "loadingBelt7",
                  "loadingBelt12",
                ].includes(system.subtype),
            )
            .map((system) => {
              const installedCount = systems.filter(
                (s) => s.subtype === system.subtype,
              ).length;
              const isInstalled = installedCount > 0;
              let cardCost = system.cost;
              let cardTonnage = system.tonnage;
              let cardPower = system.power;
              let cardProperties = { ...system.properties };
              let cardError: string | null = null;
              let customInput = null;

              // Handle dynamic fields for certain systems
              if (system.subtype === "cargoCrane") {
                const stats = cargoCraneStats;
                cardTonnage = stats.tonnage;
                cardCost = stats.cost;
                cardPower = stats.power;
                cardProperties = {
                  ...system.properties,
                  cargoSpace: Number(availableCargoSpace),
                };
              }
              if (system.subtype === "externalCargoMount") {
                const stats = externalCargoMountStats;
                cardCost = stats.cost;
                cardTonnage = stats.tonnage;
                cardPower = stats.power;
                cardProperties = {
                  ...system.properties,
                  externalCargoCapacity,
                };
                customInput = (
                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      External Cargo Capacity (tons)
                    </label>
                    <input
                      type="number"
                      value={externalCargoCapacity || ""}
                      onChange={(e) =>
                        setExternalCargoCapacity(
                          Math.max(0, Number(e.target.value)),
                        )
                      }
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-700 dark:text-gray-100"
                      min="0"
                      step="0.1"
                      disabled={isInstalled}
                    />
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Cr1,000 per ton
                    </span>
                  </div>
                );
                if (isStreamlined)
                  cardError = "Cannot be used with streamlined hulls.";
                if (isDispersedStructure)
                  cardError = "Cannot be used with dispersed structure hulls.";
              }
              if (
                system.subtype === "jumpNetInterplanetary" ||
                system.subtype === "jumpNetInterstellar"
              ) {
                const stats = jumpNetStats;
                cardCost = stats.cost;
                cardTonnage = stats.tonnage;
                cardPower = stats.power;
                cardProperties = { ...system.properties, jumpNetCargoCapacity };
                customInput = (
                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Jump Net Cargo Capacity (tons)
                    </label>
                    <input
                      type="number"
                      value={jumpNetCargoCapacity || ""}
                      onChange={(e) =>
                        setJumpNetCargoCapacity(
                          Math.max(0, Number(e.target.value)),
                        )
                      }
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-700 dark:text-gray-100"
                      min="0"
                      step="0.1"
                      disabled={isInstalled}
                    />
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {system.subtype === "jumpNetInterplanetary"
                        ? "Cr100,000 per ton, 1 per 100 tons"
                        : "Cr300,000 per ton, 1 per 100 tons"}
                    </span>
                  </div>
                );
              }

              // Validation for Add button
              let canAdd = !cardError; // Allow multiple installations for most systems
              if (
                system.subtype === "externalCargoMount" &&
                (isStreamlined || isDispersedStructure)
              )
                canAdd = false;

              // --- Refactored Add button logic ---
              const handleAdd = () => {
                let systemToAdd: CargoSystem;
                if (system.subtype === "externalCargoMount") {
                  systemToAdd = {
                    id: `cargo-${Date.now()}`,
                    type: "cargo",
                    subtype: system.subtype,
                    name: system.name,
                    description: system.description,
                    cost: cardCost,
                    tonnage: cardTonnage,
                    power: cardPower,
                    techLevel: system.techLevel,
                    properties: cardProperties,
                  };
                } else if (
                  system.subtype === "jumpNetInterplanetary" ||
                  system.subtype === "jumpNetInterstellar"
                ) {
                  systemToAdd = {
                    id: `cargo-${Date.now()}`,
                    type: "cargo",
                    subtype: system.subtype,
                    name: system.name,
                    description: system.description,
                    cost: cardCost,
                    tonnage: cardTonnage,
                    power: cardPower,
                    techLevel: system.techLevel,
                    properties: cardProperties,
                  };
                } else if (system.subtype === "cargoCrane") {
                  systemToAdd = {
                    id: `cargo-${Date.now()}`,
                    type: "cargo",
                    subtype: system.subtype,
                    name: system.name,
                    description: system.description,
                    cost: cardCost,
                    tonnage: cardTonnage,
                    power: cardPower,
                    techLevel: system.techLevel,
                    properties: cardProperties,
                  };
                } else {
                  // Static systems (Cargo Net, Cargo Scoop, Loading Belts, etc.)
                  systemToAdd = {
                    id: `cargo-${Date.now()}`,
                    type: "cargo",
                    subtype: system.subtype,
                    name: system.name,
                    description: system.description,
                    cost: cardCost,
                    tonnage: cardTonnage,
                    power: cardPower,
                    techLevel: system.techLevel,
                    properties: cardProperties,
                  };
                }
                dispatch(addCargoSystem(systemToAdd));
              };

              return (
                <div
                  key={system.subtype}
                  className="border rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                >
                  <div>
                    <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                      {system.name}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                      <div>Cost: {formatCurrency(cardCost)}</div>
                      <div>Tonnage: {cardTonnage}</div>
                      <div>Power: {cardPower}</div>
                    </div>
                    {customInput}
                    {cardError && (
                      <div className="text-xs text-red-600 dark:text-red-400 mb-2">
                        {cardError}
                      </div>
                    )}
                  </div>
                  <button
                    className={`mt-2 px-3 py-1 rounded text-white font-semibold ${!canAdd ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                    onClick={handleAdd}
                    disabled={!canAdd}
                  >
                    Add
                  </button>
                  {installedCount > 0 && (
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-300 text-center">
                      {installedCount} installed
                    </div>
                  )}
                </div>
              );
            })}
          {/* Jump Net (merged card) */}
          {(() => {
            // Only show if at least one jump net type is available
            const jumpNetTypes = [
              {
                subtype: "jumpNetInterplanetary",
                label: "Interplanetary",
                minTL: 8,
              },
              {
                subtype: "jumpNetInterstellar",
                label: "Interstellar",
                minTL: 10,
              },
            ].filter((jn) => techLevel >= jn.minTL);
            if (jumpNetTypes.length === 0) return null;

            // Use the top-level state for jumpNetType
            const jumpNetSystem = availableSystems.find(
              (s) => s.subtype === jumpNetType,
            );
            if (!jumpNetSystem) return null;
            const isInstalled = systems.some((s) => s.subtype === jumpNetType);
            const isInterstellar = jumpNetType === "jumpNetInterstellar";
            const stats = jumpNetStats;
            const cardCost = stats.cost;
            const cardTonnage = stats.tonnage;
            const cardPower = stats.power;
            const cardProperties = {
              ...jumpNetSystem.properties,
              jumpNetCargoCapacity,
            };
            const canAdd = !isInstalled;

            const handleAdd = () => {
              const systemToAdd: CargoSystem = {
                id: `cargo-${Date.now()}`,
                type: "cargo",
                subtype: jumpNetType as
                  | "jumpNetInterplanetary"
                  | "jumpNetInterstellar",
                name: jumpNetSystem.name,
                description: jumpNetSystem.description,
                cost: cardCost,
                tonnage: cardTonnage,
                power: cardPower,
                techLevel: jumpNetSystem.techLevel,
                properties: cardProperties,
              };
              dispatch(addCargoSystem(systemToAdd));
            };

            return (
              <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 flex flex-col justify-between h-full shadow">
                <div>
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">Jump Net</h4>
                    <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                      TL{jumpNetSystem.techLevel}
                    </span>
                  </div>
                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Jump Net Type
                    </label>
                    <select
                      value={jumpNetType}
                      onChange={(e) =>
                        setJumpNetType(
                          e.target.value as
                            | "jumpNetInterplanetary"
                            | "jumpNetInterstellar",
                        )
                      }
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-600 dark:text-gray-100"
                    >
                      {jumpNetTypes.map((jn) => (
                        <option key={jn.subtype} value={jn.subtype}>
                          {jn.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Jump Net Cargo Capacity (tons)
                    </label>
                    <input
                      type="number"
                      value={jumpNetCargoCapacity || ""}
                      onChange={(e) =>
                        setJumpNetCargoCapacity(
                          Math.max(0, Number(e.target.value)),
                        )
                      }
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-600 dark:text-gray-100"
                      min="0"
                      step="0.1"
                      disabled={isInstalled}
                    />
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {isInterstellar
                        ? "Cr300,000 per ton, 1 per 100 tons"
                        : "Cr100,000 per ton, 1 per 100 tons"}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-300 space-y-1 mb-2">
                    <div>Cost: {formatCurrency(cardCost)}</div>
                    <div>Tonnage: {cardTonnage}</div>
                    <div>Power: {cardPower}</div>
                  </div>
                </div>
                <div className="mt-2">
                  {!isInstalled ? (
                    <button
                      onClick={handleAdd}
                      className={`w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50`}
                      disabled={!canAdd}
                    >
                      Add
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSubtypeChange(jumpNetType)}
                      className="w-full bg-red-100 text-red-700 px-4 py-2 rounded hover:bg-red-200"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })()}
          {/* Loading Belt (merged card) */}
          {(() => {
            // Only show if at least one loading belt type is available
            const loadingBeltTypes = [
              { subtype: "loadingBelt7", label: "10 crew", minTL: 7 },
              { subtype: "loadingBelt12", label: "25 crew", minTL: 12 },
            ].filter((lb) => techLevel >= lb.minTL);
            if (loadingBeltTypes.length === 0) return null;

            // Use the top-level state for loadingBeltType
            const loadingBeltSystem = availableSystems.find(
              (s) => s.subtype === loadingBeltType,
            );
            if (!loadingBeltSystem) return null;
            const installedCount = systems.filter(
              (s) => s.subtype === loadingBeltType,
            ).length;
            const cardCost = loadingBeltSystem.cost;
            const cardTonnage = loadingBeltSystem.tonnage;
            const cardPower = loadingBeltSystem.power;
            const cardProperties = { ...loadingBeltSystem.properties };
            const canAdd = true; // Allow multiple installations

            const handleAdd = () => {
              const systemToAdd: CargoSystem = {
                id: `cargo-${Date.now()}`,
                type: "cargo",
                subtype: loadingBeltType,
                name: loadingBeltSystem.name,
                description: loadingBeltSystem.description,
                cost: cardCost,
                tonnage: cardTonnage,
                power: cardPower,
                techLevel: loadingBeltSystem.techLevel,
                properties: cardProperties,
              };
              dispatch(addCargoSystem(systemToAdd));
            };

            return (
              <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 flex flex-col justify-between h-full shadow">
                <div>
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">Loading Belt</h4>
                    <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                      TL{loadingBeltSystem.techLevel}
                    </span>
                  </div>
                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Loading Belt Type
                    </label>
                    <select
                      value={loadingBeltType}
                      onChange={(e) =>
                        setLoadingBeltType(
                          e.target.value as "loadingBelt7" | "loadingBelt12",
                        )
                      }
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-600 dark:text-gray-100"
                    >
                      {loadingBeltTypes.map((lb) => (
                        <option key={lb.subtype} value={lb.subtype}>
                          {lb.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-300 space-y-1 mb-2">
                    <div>Cost: {formatCurrency(cardCost)}</div>
                    <div>Tonnage: {cardTonnage}</div>
                    <div>Power: {cardPower}</div>
                  </div>
                </div>
                <div className="mt-2">
                  <button
                    onClick={handleAdd}
                    className={`w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50`}
                    disabled={!canAdd}
                  >
                    Add
                  </button>
                  {installedCount > 0 && (
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-300 text-center">
                      {installedCount} installed
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Installed Cargo Systems section removed */}
    </div>
  );
};

export default CargoSystems;
